import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getToken } from "../api/utils";
import ExperienceMediaSlider from "../components/ExperienceMediaSlider";
import InviteCommentSection from "../components/InviteCommentSection";
import LoginModal from "../components/LoginModal";
import RSVPModal from "../components/RSVPModal";
import { getInviteRevealById, getInviteThemeById, getThemeStyleVars } from "../data/inviteThemes";
import "./InviteLanding.css";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const inferMediaType = (raw = {}, url = "") => {
  const explicitType = `${raw.type || raw.media_type || ""}`.toLowerCase();
  if (explicitType.includes("image")) return "image";
  if (explicitType.includes("video")) return "video";
  if (raw.is_video === true) return "video";

  const cleanUrl = `${url}`.split("?")[0].toLowerCase();
  if (/\.(png|jpe?g|webp|gif|svg|avif)$/i.test(cleanUrl)) return "image";
  if (/\.(mp4|webm|mov|m4v|ogv)$/i.test(cleanUrl)) return "video";
  return "image";
};

const normalizeMediaItem = (raw = {}) => {
  if (typeof raw === "string") {
    raw = { url: raw };
  }

  const url = raw.url || raw.file_path || raw.filePath || raw.video_url;
  if (!url) return null;

  return {
    id: raw.id || raw.media_id || url,
    url,
    title: raw.title || raw.caption || "",
    type: inferMediaType(raw, url),
    visibility: raw.visibility || raw.access || "public",
    poster: raw.poster,
  };
};

const normalizeComment = (raw = {}) => ({
  id:
    raw.id ||
    raw.comment_id ||
    `${raw.author?.user_id || raw.author?.id || raw.user_id || "anon"}-${raw.timestamp || raw.created_at || Date.now()}`,
  text: raw.text || raw.body || raw.comment || "",
  visibility: raw.visibility || raw.access || "public",
  createdAt: raw.created_at || raw.timestamp || new Date().toISOString(),
  likes: raw.likes ?? raw.likes_count ?? raw.like_count ?? raw.likes_total ?? 0,
  likedByCurrentUser: raw.liked_by_current_user ?? raw.liked_by_me ?? raw.liked ?? false,
  author: {
    id: raw.author?.user_id || raw.author?.id || raw.user_id || raw.created_by || "guest",
    name: raw.author?.name || raw.user_name || raw.display_name || "Guest",
  },
  replies: Array.isArray(raw.replies) ? raw.replies.map((reply) => normalizeComment(reply)) : [],
});

const addReplyToTree = (list, parentId, reply) =>
  list.map((comment) => {
    if (comment.id === parentId) {
      return { ...comment, replies: [reply, ...(comment.replies || [])] };
    }
    if (comment.replies) {
      const updatedReplies = addReplyToTree(comment.replies, parentId, reply);
      if (updatedReplies !== comment.replies) {
        return { ...comment, replies: updatedReplies };
      }
    }
    return comment;
  });

const replaceCommentById = (list, targetId, replacement) =>
  list.map((comment) => {
    if (comment.id === targetId) {
      return { ...replacement, replies: comment.replies || [] };
    }
    if (comment.replies) {
      return { ...comment, replies: replaceCommentById(comment.replies, targetId, replacement) };
    }
    return comment;
  });

const removeCommentById = (list, targetId) =>
  list
    .map((comment) => {
      if (comment.id === targetId) return null;
      if (comment.replies) {
        return { ...comment, replies: removeCommentById(comment.replies, targetId) };
      }
      return comment;
    })
    .filter(Boolean);

const toggleLikeInTree = (list, targetId) =>
  list.map((comment) => {
    if (comment.id === targetId) {
      const nextLiked = !comment.likedByCurrentUser;
      const likes = nextLiked ? (comment.likes || 0) + 1 : Math.max(0, (comment.likes || 0) - 1);
      return { ...comment, likedByCurrentUser: nextLiked, likes };
    }
    if (comment.replies) {
      return { ...comment, replies: toggleLikeInTree(comment.replies, targetId) };
    }
    return comment;
  });

export default function InviteLanding({ themeId = "classic-professional", theme, revealId }) {
  const { token, eventId: routeEventId, eventGuestId } = useParams();
  const { user, isAuthenticated, logout } = useAuth();
  const isLoggedIn = isAuthenticated;
  const activeTheme = theme || getInviteThemeById(themeId);
  const themeStyleVars = useMemo(() => getThemeStyleVars(activeTheme), [activeTheme]);

  const [guestData, setGuestData] = useState(null);
  const [isRevealVisible, setIsRevealVisible] = useState(true);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteError, setInviteError] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [mainMedia, setMainMedia] = useState(null);
  const [mediaList, setMediaList] = useState([]);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  const isHostPreview = !!guestData?.viewer?.is_host;
  const event = useMemo(() => guestData?.event || {}, [guestData]);
  const guest = useMemo(() => guestData?.guest || {}, [guestData]);
  const eventId = event?.event_id || event?.id || routeEventId;
  const canLoadComments = Boolean(eventId && !inviteError);
  const activeReveal = getInviteRevealById(revealId || event?.reveal_id || "curtain-open");

  const currentUser = useMemo(
    () => ({
      id: user?.id || user?.token || "guest",
      name: user?.name || "You",
      isHost: user?.isHost || isHostPreview || false,
    }),
    [isHostPreview, user]
  );

  const fetchInvite = useCallback(async (signal) => {
    if (!token && !routeEventId) {
      setInviteError("No invitation link or event preview provided.");
      setGuestData(null);
      setMainMedia(null);
      setMediaList([]);
      return;
    }

    setLoadingInvite(true);
    setInviteError("");

    try {
      const endpoint = token
        ? `${API_BASE}/events/invite/${token}`
        : eventGuestId
        ? `${API_BASE}/events/${routeEventId}/guests/${eventGuestId}/preview`
        : `${API_BASE}/events/${routeEventId}/experience`;
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${getToken() || ""}`,
          Accept: "application/json",
        },
        signal,
      });

      if (!response.ok) {
        let message = `Invite fetch failed (${response.status})`;
        try {
          const payload = await response.json();
          message = payload.detail || payload.message || message;
        } catch (_error) {
          // ignore JSON parse failures
        }
        throw new Error(message);
      }

      const payload = await response.json();
      setGuestData(payload);
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      setInviteError(error.message || "Invalid or expired invite link.");
      setGuestData(null);
      setMainMedia(null);
      setMediaList([]);
    } finally {
      setLoadingInvite(false);
    }
  }, [eventGuestId, routeEventId, token]);

  const fetchComments = useCallback(async () => {
    if (!eventId) {
      setComments([]);
      setCommentError("");
      setLoadingComments(false);
      return;
    }

    setLoadingComments(true);
    setCommentError("");
    try {
      const headers = { Accept: "application/json" };
      const bearer = getToken();
      if (bearer) headers.Authorization = `Bearer ${bearer}`;
      const response = await fetch(`${API_BASE}/events/${eventId}/comments`, { headers });
      if (!response.ok) {
        throw new Error("Unable to load comments");
      }
      const payload = await response.json();
      const rawComments = Array.isArray(payload) ? payload : payload.comments || payload.data || [];
      setComments(rawComments.map((item) => normalizeComment(item)));
      setPendingDeleteIds([]);
    } catch (error) {
      setCommentError(error.message || "Unable to load comments.");
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [eventId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchInvite(controller.signal);
    return () => controller.abort();
  }, [fetchInvite, isLoggedIn]);

  useEffect(() => {
    setIsRevealVisible(true);
    const revealDuration = activeReveal.id === "petal-blessing" ? 5600 : 2600;
    const timer = window.setTimeout(() => setIsRevealVisible(false), revealDuration);
    return () => window.clearTimeout(timer);
  }, [activeReveal.id, eventId]);

  useEffect(() => {
    if (!guestData) {
      setMainMedia(null);
      setMediaList([]);
      return;
    }

    const heroUrl = event.cover_media || event.image_url || event.preview;
    const hero = heroUrl
      ? {
          id: event.id || heroUrl,
          url: heroUrl,
          type: inferMediaType(event, heroUrl),
          visibility: "public",
        }
      : null;

    const normalizedMedia = (Array.isArray(guestData.media) ? guestData.media : [])
      .map((item) => normalizeMediaItem(item))
      .filter((item) => item?.url);

    setMainMedia(hero);
    setMediaList(
      normalizedMedia.filter((item) => !hero || (item.id !== hero.id && item.url !== hero.url))
    );
  }, [event, guestData]);

  useEffect(() => {
    if (!canLoadComments) {
      setComments([]);
      setCommentError("");
      setLoadingComments(false);
      return;
    }

    fetchComments();
  }, [canLoadComments, fetchComments]);

  const handleLoginSuccess = () => {
    setShowLogin(false);
    fetchInvite();
  };

  const handleRSVPClick = () => {
    if (isHostPreview) return;
    setShowRSVPModal(true);
  };

  const handleRSVPSuccess = useCallback(() => {
    fetchInvite();
  }, [fetchInvite]);

  const handleCommentSubmit = async ({ text, visibility, parentId }) => {
    if (!isLoggedIn) {
      setShowLogin(true);
      return;
    }

    setCommentError("");
    const tempId = `temp-${Date.now()}`;
    const optimisticComment = {
      id: tempId,
      text,
      visibility,
      createdAt: new Date().toISOString(),
      likes: 0,
      likedByCurrentUser: false,
      author: {
        id: currentUser.id,
        name: currentUser.name,
      },
      replies: [],
    };

    setComments((prev) =>
      parentId ? addReplyToTree(prev, parentId, optimisticComment) : [optimisticComment, ...prev]
    );

    try {
      const headers = { "Content-Type": "application/json" };
      const bearer = getToken();
      if (bearer) headers.Authorization = `Bearer ${bearer}`;
      const endpoint = parentId
        ? `${API_BASE}/comments/${parentId}/replies`
        : `${API_BASE}/events/${eventId}/comments`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          body: text,
          visibility,
        }),
      });
      if (!response.ok) {
        throw new Error("Unable to post comment");
      }
      const saved = await response.json();
      setComments((prev) => replaceCommentById(prev, tempId, normalizeComment(saved)));
    } catch (error) {
      setCommentError(error.message || "Unable to post comment, please retry.");
      setComments((prev) => removeCommentById(prev, tempId));
    }
  };

  const handleLikeToggle = async (commentId) => {
    if (!isLoggedIn) {
      setShowLogin(true);
      return;
    }

    setComments((prev) => toggleLikeInTree(prev, commentId));
    try {
      const headers = { "Content-Type": "application/json" };
      const bearer = getToken();
      if (bearer) headers.Authorization = `Bearer ${bearer}`;
      await fetch(`${API_BASE}/comments/${commentId}/like`, {
        method: "POST",
        headers,
        body: JSON.stringify({ event_id: eventId, event_token: token }),
      });
    } catch (_error) {
      setComments((prev) => toggleLikeInTree(prev, commentId));
    }
  };

  const handleDeleteComment = useCallback(
    async (commentId) => {
      if (!isLoggedIn) {
        setShowLogin(true);
        return;
      }

      if (!eventId || pendingDeleteIds.includes(commentId)) {
        return;
      }

      if (!window.confirm("Delete this comment?")) {
        return;
      }

      setPendingDeleteIds((prev) => [...prev, commentId]);
      setCommentError("");

      try {
        const headers = { "Content-Type": "application/json" };
        const bearer = getToken();
        if (bearer) headers.Authorization = `Bearer ${bearer}`;
        const response = await fetch(`${API_BASE}/comments/${commentId}`, {
          method: "DELETE",
          headers,
          body: JSON.stringify({ event_id: eventId, event_token: token }),
        });
        if (!response.ok) {
          throw new Error("Unable to delete comment");
        }
        setComments((prev) => removeCommentById(prev, commentId));
        setPendingDeleteIds((prev) => prev.filter((id) => id !== commentId));
      } catch (error) {
        setCommentError(error.message || "Unable to delete comment.");
        setPendingDeleteIds((prev) => prev.filter((id) => id !== commentId));
        fetchComments();
      }
    },
    [eventId, fetchComments, isLoggedIn, pendingDeleteIds, token]
  );

  const guestForRSVP = useMemo(() => ({
    event_guest_id: guest.event_guest_id || guest.id || "0",
    rsvp_status: guest.rsvp_status || "pending",
    rsvp_adult_count: guest.rsvp_adult_count ?? "",
    rsvp_kids_count: guest.rsvp_kids_count ?? "",
    rsvp_allergies: guest.rsvp_allergies || "",
    rsvp_message: guest.rsvp_message || "",
  }), [guest]);

  const hasRSVPed = !!guest.rsvp_status && guest.rsvp_status !== "pending";
  const eventTitle = event.event_title || event.title || "You're Invited";
  const hostName = event.host_name || event.host || event.organizer_name || "";
  const guestName = isHostPreview
    ? event.host_name || "Host Preview"
    : guest.guest_name || guest.name || "Honoured Guest";
  const formattedDate = useMemo(() => {
    const sourceDate = event.event_date || event.date;
    if (!sourceDate) return "Date to be announced";
    const parsed = new Date(sourceDate);
    if (Number.isNaN(parsed.getTime())) return String(sourceDate);
    return parsed.toLocaleDateString("en-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [event.date, event.event_date]);
  const formattedTime = event.event_time || event.time || "";

  if (loadingInvite) return <p>Loading your invitation...</p>;
  if (inviteError) return <p className="error-text">{inviteError}</p>;
  if (!guestData) return <p>No invitation data available.</p>;

  return (
    <div className="invite-landing" style={themeStyleVars}>
      {isRevealVisible ? (
        <div className={`landing-reveal-overlay reveal-${activeReveal.id}`} aria-hidden="true">
          <div className="landing-reveal-glow" />
          {activeReveal.id === "curtain-open" ? (
            <>
              <div className="landing-reveal-curtain landing-reveal-curtain-left" />
              <div className="landing-reveal-curtain landing-reveal-curtain-right" />
            </>
          ) : null}
          {activeReveal.id === "confetti-pop" ? (
            <div className="landing-reveal-confetti">
              {[...Array(18)].map((_, index) => (
                <span
                  key={index}
                  style={{
                    "--i": index,
                    left: `${8 + ((index * 11) % 84)}%`,
                    top: `${15 + ((index * 17) % 68)}%`,
                  }}
                />
              ))}
            </div>
          ) : null}
          {activeReveal.id === "spotlight-rise" ? <div className="landing-reveal-spotlight" /> : null}
          {activeReveal.id === "floral-bloom" ? (
            <div className="landing-reveal-florals">
              <span />
              <span />
              <span />
            </div>
          ) : null}
          {activeReveal.id === "slide-up-card" ? <div className="landing-reveal-card" /> : null}
          {activeReveal.id === "sparkle-frame" ? (
            <div className="landing-reveal-frame">
              <span />
              <span />
              <span />
            </div>
          ) : null}
          {activeReveal.id === "balloon-float" ? (
            <div className="landing-reveal-balloons">
              {[...Array(11)].map((_, index) => (
                <span key={index} style={{ "--i": index }} />
              ))}
            </div>
          ) : null}
          {activeReveal.id === "petal-blessing" ? (
            <div className="landing-reveal-petals">
              {[...Array(84)].map((_, index) => (
                <span
                  key={index}
                  style={{
                    "--i": index,
                    "--petal-left": `${4 + ((index * 13) % 92)}%`,
                    "--petal-size": `${18 + (index % 5) * 4}px`,
                    "--petal-drift-mid": `${((index % 7) - 3) * 16}px`,
                    "--petal-drift-end": `${((index % 9) - 4) * 24}px`,
                  }}
                >
                  {["✿", "✽", "✼", "❀"][index % 4]}
                </span>
              ))}
            </div>
          ) : null}
          <div className="landing-reveal-title">
            <strong>{eventTitle}</strong>
          </div>
        </div>
      ) : null}
      <div className="invite-landing-shell">
        <header className="landing-topbar">
          <div>
            <h1>{eventTitle}</h1>
          </div>
          <div className="landing-topbar-actions">
            {isHostPreview && <span className="landing-host-pill">Host Preview Mode</span>}
            {!isHostPreview && guest.login_required && !isLoggedIn ? (
              <button type="button" onClick={() => setShowLogin(true)} className="landing-secondary-button">
                Login
              </button>
            ) : !isHostPreview && isLoggedIn ? (
              <button type="button" onClick={logout} className="landing-secondary-button">
                Logout
              </button>
            ) : null}
          </div>
        </header>

        <section className="landing-hero-card">
          <div className="landing-slider-panel landing-slider-panel-hero">
            <ExperienceMediaSlider
              mainMedia={mainMedia}
              additionalMedia={mediaList}
              isLoggedIn={isLoggedIn}
              onLoginPrompt={() => setShowLogin(true)}
              clientConfig={{ maxHeight: 760, minHeight: 520 }}
              shouldAutoplayPrimary={!isRevealVisible}
            />
          </div>

          <div className="landing-hero-copy">
            <p className="landing-eyebrow">Invited Guest</p>
            <h2>{guestName}</h2>
            <div className="landing-detail-grid">
              {hostName ? (
                <div>
                  <span>Hosted by</span>
                  <strong>{hostName}</strong>
                </div>
              ) : null}
              <div>
                <span>Date</span>
                <strong>{formattedDate}</strong>
              </div>
              {formattedTime ? (
                <div>
                  <span>Time</span>
                  <strong>{formattedTime}</strong>
                </div>
              ) : null}
              <div>
                <span>Venue</span>
                <strong>{event.location || "Venue to be announced"}</strong>
              </div>
            </div>
            {!isHostPreview && (
              <button type="button" onClick={handleRSVPClick} className="landing-primary-button">
                {hasRSVPed ? "Change RSVP" : "RSVP Now"}
              </button>
            )}
          </div>
        </section>

        <section className="landing-notes-card">
          <div>
            <p className="landing-eyebrow">Event Note</p>
            <h2>Everything you need in one place</h2>
            <p>
              Watch the invitation film, confirm your RSVP, and leave your wishes for the hosts in
              a single experience.
            </p>
          </div>
        </section>

        {loadingComments && <p className="landing-muted-text">Loading comments...</p>}
        {commentError && <p className="error-text">{commentError}</p>}

        {isLoggedIn ? (
          <InviteCommentSection
            comments={comments}
            currentUser={currentUser}
            onSubmitComment={handleCommentSubmit}
            onLikeToggle={handleLikeToggle}
            onDeleteComment={handleDeleteComment}
            pendingDeleteIds={pendingDeleteIds}
          />
        ) : (
          <div className="landing-login-card">
            <p>Login to read guest wishes and leave a note for the hosts.</p>
            <button type="button" className="landing-primary-button" onClick={() => setShowLogin(true)}>
              Login / Sign up
            </button>
          </div>
        )}

        <footer className="invite-site-footer">
          <p>Create your own video invitation with DesiInvite.</p>
          <div className="invite-site-footer-actions">
            <a href="/">Explore DesiInvite</a>
          </div>
        </footer>
      </div>

      {showRSVPModal && (
        <div className="landing-modal-overlay" role="dialog" aria-modal="true">
          <div className="landing-modal-card">
            <button
              type="button"
              className="landing-modal-close"
              aria-label="Close RSVP modal"
              onClick={() => setShowRSVPModal(false)}
            >
              x
            </button>
            <RSVPModal
              guest={guestForRSVP}
              inviteToken={token}
              onClose={() => setShowRSVPModal(false)}
              onSuccess={handleRSVPSuccess}
            />
          </div>
        </div>
      )}

      {showLogin && (
        <LoginModal
          defaultValue={guest.email || guest.email_id || guest.phone || ""}
          onClose={() => setShowLogin(false)}
          onSuccess={handleLoginSuccess}
        />
      )}
    </div>
  );
}
